import { App as CapacitorApp } from '@capacitor/app';

import Config from '@/config';
import ls, {
  ACCESS_TOKEN_KEY,
  DEVICE_TOKEN_KEY,
  REFRESH_LOCK_KEY,
} from '@/util/localstorage';
import { isWeb } from '@/util/platform';
import { unidentify as unidentifyVexo } from '@/util/vexo';

const CLIENT_HEADER = 'x-client';
const CLIENT_VERSION_HEADER = 'x-client-version';
const REFRESH_HEADER = 'x-refresh';
const NEW_TOKEN_HEADER = 'x-refreshed-token';

const MAX_REFRESH_FAILURES = 3;
const LOCK_STALE_MS = 10_000;
const LOCK_WAIT_TIMEOUT_MS = 5_000;
const REFRESH_BUFFER_MS = 30_000;
const WEB_LOCK_KEY = 'token_refresh';

const clientVersion = `${Config.BUILD_VERSION}-${Config.BUILD_COMMIT}`;

function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function getExp(token: string): number | null {
  const payload = decodeTokenPayload(token);
  if (payload && typeof payload.exp === 'number') return payload.exp;
  return null;
}

class TokenManager {
  private static _instance: TokenManager | null = null;

  private _refreshPromise: Promise<string | null> | null = null;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _consecutiveFailures = 0;
  private _appStateListener: { remove: () => Promise<void> } | null = null;

  private _storageHandler = (e: StorageEvent) => {
    if (e.key === ACCESS_TOKEN_KEY) {
      if (e.newValue) {
        this.handleNewToken(e.newValue);
      } else {
        this.logout();
      }
    }
  };

  private _visibilityHandler = () => {
    if (document.visibilityState === 'visible' && this.isTokenExpired()) {
      this.ensureFreshToken();
    }
  };

  static getInstance(): TokenManager {
    if (!TokenManager._instance) {
      TokenManager._instance = new TokenManager();
    }
    return TokenManager._instance;
  }

  private constructor() {
    window.addEventListener('storage', this._storageHandler);
    document.addEventListener('visibilitychange', this._visibilityHandler);

    if (!isWeb()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive && this.isTokenExpired()) {
          this.ensureFreshToken();
        }
      }).then((listener) => {
        this._appStateListener = listener;
      });
    }
  }

  getAccessToken(): string | null {
    return ls.get(ACCESS_TOKEN_KEY);
  }

  getDeviceToken(): string | null {
    return ls.get(DEVICE_TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;
    const exp = getExp(token);
    if (exp === null) return true;
    return Date.now() / 1000 >= exp;
  }

  handleNewToken(token: string): void {
    const current = this.getAccessToken();
    if (current === token) return;

    const newExp = getExp(token);
    if (newExp === null) return;

    if (current) {
      const currentExp = getExp(current);
      if (currentExp !== null && newExp < currentExp) return;
    }

    ls.set(ACCESS_TOKEN_KEY, token);
    this._consecutiveFailures = 0;
    this.startRefreshTimer();
  }

  async ensureFreshToken(): Promise<string | null> {
    if (this._refreshPromise) return this._refreshPromise;

    const current = this.getAccessToken();
    if (current && !this.isTokenExpired()) return current;

    if (!this.getDeviceToken()) {
      this.logout();
      return null;
    }

    if (navigator.locks) {
      return this._refreshWithWebLock();
    }

    return this._refreshWithLocalStorageLock();
  }

  startRefreshTimer(): void {
    this.stopRefreshTimer();

    const token = this.getAccessToken();
    if (!token) return;

    const exp = getExp(token);
    if (exp === null) return;

    const delayMs = exp * 1000 - Date.now() - REFRESH_BUFFER_MS;
    if (delayMs <= 0) {
      this.ensureFreshToken();
      return;
    }

    this._refreshTimer = setTimeout(() => {
      this.ensureFreshToken();
    }, delayMs);
  }

  stopRefreshTimer(): void {
    if (this._refreshTimer !== null) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  cleanup(): void {
    this.stopRefreshTimer();
    this._refreshPromise = null;
    this._consecutiveFailures = 0;
    ls.clearAuth();
  }

  logout(): void {
    // Vexo: break the session→user link before the hard navigation tears
    // down the JS context. unidentify() guards on window.vexo so it's a
    // no-op when the SDK isn't loaded.
    unidentifyVexo();
    this.cleanup();
    const path = isWeb() ? '/login' : '/app-login';
    window.location.href = path;
  }

  destroy(): void {
    this.stopRefreshTimer();
    window.removeEventListener('storage', this._storageHandler);
    document.removeEventListener('visibilitychange', this._visibilityHandler);
    if (this._appStateListener) {
      this._appStateListener.remove();
      this._appStateListener = null;
    }
    TokenManager._instance = null;
  }

  private _refreshWithWebLock(): Promise<string | null> {
    const doRefresh = async (): Promise<string | null> => {
      let result: string | null = null;
      await navigator.locks.request(WEB_LOCK_KEY, async () => {
        const current = this.getAccessToken();
        if (current && !this.isTokenExpired()) {
          result = current;
          return;
        }
        result = await this._performRefresh();
      });
      return result;
    };

    this._refreshPromise = doRefresh().finally(() => {
      this._refreshPromise = null;
    });

    return this._refreshPromise;
  }

  private async _refreshWithLocalStorageLock(): Promise<string | null> {
    if (this._isLockHeld()) {
      const token = await this._waitForExternalRefresh();
      if (token) return token;
    }

    this._refreshPromise = this._performRefresh(true).finally(() => {
      this._refreshPromise = null;
    });
    return this._refreshPromise;
  }

  private _isLockHeld(): boolean {
    const lock = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!lock) return false;
    const ts = parseInt(lock, 10);
    if (isNaN(ts)) return false;
    return Date.now() - ts < LOCK_STALE_MS;
  }

  private _acquireLock(): void {
    localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
  }

  private _releaseLock(): void {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }

  private _waitForExternalRefresh(): Promise<string | null> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('storage', handler);
        resolve(null);
      }, LOCK_WAIT_TIMEOUT_MS);

      const handler = (e: StorageEvent) => {
        if (e.key === ACCESS_TOKEN_KEY && e.newValue) {
          clearTimeout(timeout);
          window.removeEventListener('storage', handler);
          resolve(e.newValue);
        }
      };

      window.addEventListener('storage', handler);
    });
  }

  private async _performRefresh(
    useLocalStorageLock = false,
  ): Promise<string | null> {
    const accessToken = this.getAccessToken();
    const deviceToken = this.getDeviceToken();

    if (!deviceToken) {
      this._onRefreshFailure();
      return null;
    }

    if (useLocalStorageLock) this._acquireLock();

    try {
      const response = await fetch(Config.GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: accessToken || '',
          [REFRESH_HEADER]: deviceToken,
          [CLIENT_HEADER]: 'area2k-app',
          [CLIENT_VERSION_HEADER]: clientVersion,
        },
        body: JSON.stringify({ query: '{ __typename }' }),
      });

      const newToken = response.headers.get(NEW_TOKEN_HEADER);

      if (newToken) {
        this.handleNewToken(newToken);
        return newToken;
      }

      this._onRefreshFailure();
      return null;
    } catch {
      this._onRefreshFailure();
      return null;
    } finally {
      if (useLocalStorageLock) this._releaseLock();
    }
  }

  private _onRefreshFailure(): void {
    this._consecutiveFailures++;
    if (this._consecutiveFailures >= MAX_REFRESH_FAILURES) {
      this.logout();
    }
  }
}

export default TokenManager;
